/**
 * Mirrors MacroVolumeConfigUtils.__GetMacroPhaseTypeIdToWeekCountHashMap in
 * backend/serverless/program/classes/kinesis_utils_class.py
 *
 * Percentage phases share the non-static week pool. They are assigned
 * lowest-percentage first; the highest-percentage phase is assigned last
 * and absorbs any rounding remainder. Every percentage phase is at least
 * 2 weeks (we do not have volume data for 1-week blocks).
 */
export function getMacroPhaseTypeIdToWeekCountHashMap(
    macroVolumePhaseTypes,
    macroVolumePhases,
    pureProgramLength,
    deloadAfterEvent = true,
) {
    const MIN_PERCENTAGE_PHASE_WEEKS = 2

    const knownTypeIds = new Set(
        macroVolumePhaseTypes.map((phaseType) => phaseType.macroVolumePhaseTypeId),
    )

    const lengthBucketConfigs = [...macroVolumePhases].sort((a, b) => a.sortOrder - b.sortOrder)

    if (lengthBucketConfigs.length === 0) {
        return {}
    }

    const configByTypeId = {}

    for (const config of lengthBucketConfigs) {
        const typeId = config.macroVolumePhaseTypeId

        if (!knownTypeIds.has(typeId)) {
            continue
        }

        configByTypeId[typeId] = {
            type: config.configDurationType,
            value: Number(config.configDuration),
            sortOrder: config.sortOrder,
        }
    }

    const weekCountByTypeId = {}

    for (const phaseType of [...macroVolumePhaseTypes].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    )) {
        weekCountByTypeId[phaseType.macroVolumePhaseTypeId] = 0
    }

    // Static weeks are reserved first. Percentage phases split what remains.
    let percentageWeekPool = pureProgramLength

    for (const [typeId, config] of Object.entries(configByTypeId)) {
        if (config.type === 'static') {
            percentageWeekPool -= config.value
            weekCountByTypeId[typeId] = config.value
        } else if (config.type === 'static-addon') {
            // Post-race weeks sit outside the main season budget.
            weekCountByTypeId[typeId] = deloadAfterEvent ? config.value : 0
        }
    }

    // Percentage phases: lowest % first, highest % last (absorbs remainder).
    const percentageConfigs = Object.entries(configByTypeId)
        .filter(([, config]) => config.type === 'percentage')
        .sort(([, a], [, b]) => a.value - b.value)

    let remainingPercentageWeeks = percentageWeekPool

    percentageConfigs.forEach(([typeId, config], index) => {
        const isHighestPercentagePhase = index === percentageConfigs.length - 1

        let weeks

        if (isHighestPercentagePhase) {
            // Last / largest % phase takes whatever is left so the pool adds up.
            weeks = remainingPercentageWeeks
        } else {
            const phasesAfterThis = percentageConfigs.length - index - 1
            // Leave enough room for later phases to each stay >= 2 weeks.
            const maxWeeks = remainingPercentageWeeks - (MIN_PERCENTAGE_PHASE_WEEKS * phasesAfterThis)
            const targetWeeks = Math.max(
                MIN_PERCENTAGE_PHASE_WEEKS,
                Math.round(percentageWeekPool * config.value),
            )
            weeks = Math.max(0, Math.min(targetWeeks, maxWeeks))
        }

        weekCountByTypeId[typeId] = weeks
        remainingPercentageWeeks -= weeks
    })

    return Object.fromEntries(
        Object.entries(weekCountByTypeId).filter(([, weekCount]) => weekCount !== 0),
    )
}

export function formatMacroVolumePhaseDuration(durationType, duration) {
    const numericDuration = Number(duration)

    switch (durationType) {
        case 'percentage':
            return `${Math.round(numericDuration * 100)}%`
        case 'static':
            return `${numericDuration} wk${numericDuration === 1 ? '' : 's'}`
        case 'static-addon':
            return `+${numericDuration} wk${numericDuration === 1 ? '' : 's'}`
        default:
            return String(numericDuration)
    }
}

export function buildMacroVolumePhaseSegments({
    macroVolumePhaseTypes,
    macroVolumePhases,
    macroVolumePhaseTypeHashMap,
    programLength,
    deloadAfterEvent = true,
}) {
    const weekCounts = getMacroPhaseTypeIdToWeekCountHashMap(
        macroVolumePhaseTypes,
        macroVolumePhases,
        programLength,
        deloadAfterEvent,
    )

    const phaseConfigByTypeId = Object.fromEntries(
        macroVolumePhases.map((phase) => [phase.macroVolumePhaseTypeId, phase]),
    )

    const segments = []

    for (const phaseType of [...macroVolumePhaseTypes].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    )) {
        const typeId = phaseType.macroVolumePhaseTypeId
        const weeks = weekCounts[typeId] ?? 0
        const phaseConfig = phaseConfigByTypeId[typeId]

        if (!phaseConfig || weeks <= 0) {
            continue
        }

        const phaseTypeMeta = macroVolumePhaseTypeHashMap?.[typeId] ?? phaseType

        segments.push({
            macroVolumePhaseTypeId: typeId,
            name: phaseTypeMeta.name,
            colorHex: phaseTypeMeta.colorHex,
            weeks,
            configDurationType: phaseConfig.configDurationType,
            configDuration: Number(phaseConfig.configDuration),
            durationLabel: formatMacroVolumePhaseDuration(
                phaseConfig.configDurationType,
                phaseConfig.configDuration,
            ),
            isStaticAddon: phaseConfig.configDurationType === 'static-addon',
        })
    }

    const totalWeeks = segments.reduce((sum, segment) => sum + segment.weeks, 0)

    return { segments, totalWeeks }
}
